import {
  reportedPropertiesToApply,
  type IRealtimeEntity,
  type RealtimeEntityPropertyValue,
} from '@interfaces/realtimeEntities';

import {
  reportCarriesTransform,
  transformEchoesStored,
  type RealtimeEntityReport,
  type RealtimeEntityTransform,
} from './realtimeEntitiesUnreal';

/** What one inbound engine report should do to the registry. */
export interface RealtimeEntityReportPlan {
  /** Transform to publish and merge, or `null` when the report moved nothing. */
  transform: (RealtimeEntityTransform & { entityId: string }) | null;
  /**
   * Bag to apply to a never-saved entity, possibly empty; `null` when the
   * entity is saved or unknown.
   */
  reported: Record<string, RealtimeEntityPropertyValue> | null;
  /** The subset of `reported` the entity had no value for; what the inspector re-seeds. */
  filled: Record<string, RealtimeEntityPropertyValue>;
}

/**
 * Decides what an inbound UPDATE_REALTIME_ENTITY report does, without touching
 * state.
 *
 * The transform half goes to any tracked entity unless it echoes the stored
 * value at float32, as the engine's report after its own respawn does. The
 * property half goes only to a never-saved entity, where the hub's value wins
 * over the engine's echo of a class default.
 *
 * @param existing - The tracked entity the report names, if any.
 * @param report - The validated inbound report.
 * @returns The plan; every field may be empty.
 */
export const planRealtimeEntityReport = (
  existing: IRealtimeEntity | undefined,
  report: RealtimeEntityReport,
): RealtimeEntityReportPlan => {
  const { properties, ...transform } = report;
  const moves =
    reportCarriesTransform(report) &&
    (!existing || !transformEchoesStored(existing, transform));

  if (!existing || existing.saved) {
    return { transform: moves ? transform : null, reported: null, filled: {} };
  }

  const reported = properties ?? {};

  return {
    transform: moves ? transform : null,
    reported,
    filled: reportedPropertiesToApply(existing, reported),
  };
};
